import { ICategorized, ICategory, IEntity, IEquatable } from '../types';
import { isEntity } from '../utils';

const ROOT_CATEGORY = process.env.ROOT_CATEGORY || '';

/**
 * Retrieves the root category from the given list of categories.
 * @param categories - The list of categories.
 * @returns The root category, if found; otherwise undefined.
 */
export function getRootCategory(categories: ICategory[]): ICategory | undefined {
  const rootCategory = ROOT_CATEGORY && categories.find(x => x.id == ROOT_CATEGORY);
  if (rootCategory) {
    return rootCategory;
  } else {
    return categories.find(x => x.category == null); // !!! match null & undefined
  }
}

/**
 * Retrieves the parent category of an item.
 * @param categories - The array of categories.
 * @param item - The item to find the parent category for.
 * @returns The parent category of the item, or undefined if not found.
 */
export function getParentCategory(categories: ICategory[], item: ICategorized): ICategory | undefined {
  const parentCategoryId = asCategoryId(item.category);
  const parentCategory = parentCategoryId ? categories.find(x => x.id === parentCategoryId) : undefined;
  return parentCategory;
}

/**
 * Retrieves the parent categories of a category with the given ID.
 * @param categories - The list of categories.
 * @param id - The ID of the category.
 * @returns An array of parent categories.
 */
export function getParentCategoriesById(categories: ICategory[], id?: string): ICategory[] {
  const routeCategories: string[] = [];
  const segments: ICategory[] = [];
  let parentId = id;
  while (parentId != null) { // !!! loose
    const parentCategory = categories.find(c => c.id === parentId);
    if (parentCategory) {
      const segment = { ...parentCategory };
      segments.unshift(segment);
      routeCategories.push(parentId);
      parentId = asCategoryId(parentCategory.category);
      if (parentId) {
        if (routeCategories.includes(parentId)) {
          console.log('ICategory.getParentCategoriesById', 'circular reference found', parentId);
          parentId = undefined;
        }
      }
    } else {
      parentId = undefined;
    }
  }
  return segments;
}

/**
 * Retrieves the category segments for an ICategorized item.
 * @param item - The item to retrieve the category segments for.
 * @param categories - The list of categories.
 * @returns An array of category segments.
 */
export function getCategorySegments(categories: ICategory[], item: ICategorized): ICategory[] {
  const parentCategoryId = asCategoryId(item.category);
  const segments: ICategory[] = getParentCategoriesById(categories, parentCategoryId);
  // console.log('getCategorySegments.segments', segments.map(x => x.id));
  return segments;
}

/**
 * Retrieves the category segments for a category.
 * @param item - The category to retrieve the category segments for.
 * @param categories - The list of categories.
 * @returns An array of category segments.
 */
export function getCategorySegmentsByCategory(categories: ICategory[], item: ICategory): ICategory[] {
  const parentCategoryId = asCategoryId(item.category);
  const segments: ICategory[] = parentCategoryId ? getParentCategoriesById(categories, parentCategoryId) : [];
  segments.push(item);
  // console.log('getCategorySegmentsByCategory.segments', segments.map(x => x.id));
  return segments;
}

/**
 * Retrieves the ID of a category or an object that implements IEquatable.
 * @param value - The category or object to retrieve the ID from.
 * @returns The ID of the category or object as a string.
 */
export function asCategoryId(value: undefined): undefined;
export function asCategoryId(value: string): string;
export function asCategoryId(value: IEntity | IEquatable): string;
export function asCategoryId(value: IEntity | IEquatable | undefined): string | undefined;
export function asCategoryId(value: IEntity | string | number | undefined): string | undefined {
  return isEntity(value) ?
    String(value.id) :
    (value ?
      String(value) :
      undefined
    );
}

/**
 * Finds a category in the given array of categories based on the provided item.
 * @param categories - The array of categories to search in.
 * @param item - The item to find in the categories array.
 * @returns The found category, if any, or undefined.
 */
export function findCategoryInCategories(categories: ICategory[], item: ICategory | IEquatable | undefined): ICategory | undefined {
  const categoryId = asCategoryId(item);
  return categoryId ? categories.find(x => x.id === categoryId) : undefined;
}

/**
 * Finds child categories based on the given item.
 * @param categories - The array of categories to search in.
 * @param item - The item to find child categories for.
 * @returns An array of child categories.
 */
export function findChildCategories(categories: ICategory[], item: ICategory | IEquatable | undefined): ICategory[] {
  const categoryId = asCategoryId(item);
  return categoryId ? categories.filter(x => asCategoryId(x.category) === categoryId) : [];
}
